/**
 * Default keys that moved, for the players whose saved binds still have the old ones (0.93, owner decision, 22 October
 * 2026: "The emotion/reaction start by pressing X"): the voice reactions' wheel went from H to X, and cancelling an
 * action (healing) from X to H. Only binds exactly as the old defaults left them move, and never onto a key the player
 * gave something else: a player who changed either keeps theirs. No key ends up doing two things it didn't before.
 *
 * Done once: the settings saved afterwards say which defaults they have (BINDS_REVISION), and a player who then puts
 * the wheel back on H, or cancelling on X, keeps it so (0.93 review).
 */

/**
 * the defaults the saved binds have: 1 since 0.93 (the wheel on X, cancelling on H), 2 since 1.05 (the flashlight on the
 * middle mouse button), 3 since 1.06 (the jump on Space), 4 since 1.10 (the Octo Gadget's shot on Shift); none said, older
 */
export const BINDS_REVISION = 4;

const same = (list: readonly string[] | undefined, keys: readonly string[]): boolean =>
    list !== undefined && list.length === keys.length && list.every((key, index) => key === keys[index]);

/** keys no default uses, in the order they're given to an action that has to leave its key */
const CANCEL_KEYS = ["H", "Y", "J", "K", "U", "O", "P", "5"] as const;
const WHEEL_KEYS = ["J", "K", "U", "O", "P", "Y", "5"] as const;

/**
 * `binds`: every action's inputs as loaded (the defaults, then the saved ones over them), changed in place; `saved`: the
 * binds as they were saved. Whether anything moved
 */
export function moveOldDefaultBinds(binds: Record<string, string[]>, saved: Readonly<Record<string, unknown>>): boolean {
    const holders = (input: string): string[] => Object.keys(binds).filter(action => binds[action]?.includes(input));
    const has = (action: string): boolean => Object.hasOwn(saved, action);
    const firstFree = (keys: readonly string[]): string | undefined => keys.find(key => holders(key).length === 0);
    const cancelOld = has("cancel_action") && same(binds.cancel_action, ["X"]);
    const wheelOld = has("+reaction_wheel") && same(binds["+reaction_wheel"], ["H"]);
    // (saved before the wheel existed: it has its new default, X, already)
    const wheelNew = !has("+reaction_wheel");
    /** X held by nothing but the wheel and cancelling as the old defaults left it (which moves off it) */
    const xFree = (): boolean => holders("X").every(action => action === "+reaction_wheel" || (action === "cancel_action" && cancelOld));
    let moved = false;

    // the wheel from its old key to X, when nothing of the player's own is there
    if (wheelOld && xFree()) {
        binds["+reaction_wheel"] = ["X"];
        moved = true;
    }
    // the wheel given X in a save from before it, X being the player's own key for something else: a free key instead
    if (wheelNew && binds["+reaction_wheel"]?.includes("X") && !xFree()) {
        const key = firstFree(WHEEL_KEYS);
        binds["+reaction_wheel"] = key ? [key] : [];
        moved = true;
    }
    // cancelling off X when the wheel is there: to H (the wheel's old key), or the first key nothing has
    if (cancelOld && binds["+reaction_wheel"]?.includes("X")) {
        const key = firstFree(CANCEL_KEYS);
        binds.cancel_action = key ? [key] : [];
        moved = true;
    }
    return moved;
}

/** the flashlight's action (1.05) and the inputs it takes when they're free, in this order */
export const FLASHLIGHT_ACTION = "flashlight";
const FLASHLIGHT_INPUTS = ["Mouse1", "V"] as const;

/**
 * The flashlight (1.05, the owner, 28 October 2026: "it can be turned on or off by middle mouse press or v (if its
 * available the button and not used as another hotkey)"), for binds saved before it: the middle mouse button and V,
 * each only when nothing of the player's has it (V is the Gravity Gloves' merge by default, so by default the middle
 * button alone). Binds saved since have the flashlight's own (what the player set, or none), kept as they are.
 * Whether anything changed
 */
export function placeFlashlight(binds: Record<string, string[]>, saved: Readonly<Record<string, unknown>>): boolean {
    if (Object.hasOwn(saved, FLASHLIGHT_ACTION)) return false;
    const taken = (input: string): boolean => Object.keys(binds).some(action => action !== FLASHLIGHT_ACTION && binds[action]?.includes(input));
    const keys = FLASHLIGHT_INPUTS.filter(input => !taken(input));
    const before = binds[FLASHLIGHT_ACTION] ?? [];
    binds[FLASHLIGHT_ACTION] = keys;
    return !same(before, keys);
}

/** the jump's action (1.06) and the inputs it takes, the first of them that's free */
export const JUMP_ACTION = "jump";
const JUMP_INPUTS = ["Space", "J", "K", "U", "O", "P"] as const;

/**
 * The jump (1.06, the owner, 29 October 2026: "Space to jump"), for binds saved before it: Space, which also swaps guns
 * by default (out of a plane it jumps, on the ground it swaps: the two never both do something), unless the player gave
 * Space to something else; then the first of the other keys nothing has, or none (Settings has it). Binds saved since
 * have the jump's own, kept as they are. Whether anything changed
 */
export function placeJump(binds: Record<string, string[]>, saved: Readonly<Record<string, unknown>>): boolean {
    if (Object.hasOwn(saved, JUMP_ACTION)) return false;
    const free = (input: string): boolean => Object.keys(binds).every(action =>
        action === JUMP_ACTION || !binds[action]?.includes(input) || (input === "Space" && action === "other_weapon"));
    const key = JUMP_INPUTS.find(free);
    const before = binds[JUMP_ACTION] ?? [];
    binds[JUMP_ACTION] = key ? [key] : [];
    return !same(before, binds[JUMP_ACTION]);
}

/** the Octo Gadget's action (1.10) and its key */
export const OCTO_BIND_ACTION = "octo";
const OCTO_INPUT = "Shift";

/**
 * The Octo Gadget's shot (1.10, the owner, 30 October 2026: "it can also be quick used by precesing shift"), for binds
 * saved before it: Shift, unless the player gave Shift to something else; then none (Settings has it). Binds saved
 * since have its own, kept as they are. Whether anything changed
 */
export function placeOcto(binds: Record<string, string[]>, saved: Readonly<Record<string, unknown>>): boolean {
    if (Object.hasOwn(saved, OCTO_BIND_ACTION)) return false;
    const taken = Object.keys(binds).some(action => action !== OCTO_BIND_ACTION && binds[action]?.includes(OCTO_INPUT));
    const before = binds[OCTO_BIND_ACTION] ?? [];
    binds[OCTO_BIND_ACTION] = taken ? [] : [OCTO_INPUT];
    return !same(before, binds[OCTO_BIND_ACTION]);
}

/**
 * the binds as the game loads them: `binds` (the defaults) with the saved ones over them, each a list of inputs
 * (anything else, from a damaged or hand-made copy, binds nothing), then the moved defaults moved, once each (`revision`:
 * the saved settings' bindsRevision: 0.93's moves below 1, the flashlight's place below 2, the jump's below 3, the Octo
 * Gadget's below 4). Whether anything moved
 */
export function mergeSavedBinds(binds: Record<string, string[]>, saved: Readonly<Record<string, unknown>>, revision: unknown): boolean {
    for (const action of Object.keys(saved)) {
        const list = saved[action];
        binds[action] = Array.isArray(list) ? list.filter((input): input is string => typeof input === "string") : [];
    }
    const from = Number(revision);
    let moved = false;
    // (a revision that isn't a number is no revision: every move)
    if (!(from >= 1)) moved = moveOldDefaultBinds(binds, saved) || moved;
    if (!(from >= 2)) moved = placeFlashlight(binds, saved) || moved;
    if (!(from >= 3)) moved = placeJump(binds, saved) || moved;
    if (!(from >= 4)) moved = placeOcto(binds, saved) || moved;
    return moved;
}
